import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  InputSwitch,
  InputSwitchPassThroughMethodOptions,
} from 'primereact/inputswitch';

import classes from './SimeraEmployersScreen.module.css';
import CompaniesTable from './CompaniesTable/CompaniesTable';
import TableFooter from '../../TableFooter/TableFooter';
import isNumber from '../../../utils/isNumber';
import HoverTooltip from '../../Tooltip/HoverTooltip';
import { ReactComponent as ClearIcon } from '../../../icons/CloseRoundIcon.svg';
import { useGetCompaniesPaginatedQuery } from '../../../store/modules/companies';
import { CompaniesPaginatedOptions } from '../../../constants/CompaniesPaginatedOptions';
import { parseOrder } from '../../../constants/CandidatesPaginatedOptions';

interface FormData {
  search: string;
}

enum ClientsFilters {
  haveClients = 'HaveClients',
  HaveOpenPositions = 'HaveOpenPositions',
  haveWorkers = 'HaveWorkers',
}
const SimeraEmployersScreen = () => {
  const [t] = useTranslation('global');
  const [searchParams, setSearchParams] = useSearchParams();

  const companiesQuery = useMemo<CompaniesPaginatedOptions>(() => {
    const id = searchParams.get('id');
    const name = searchParams.get('name');
    const haveWorker = searchParams.get(ClientsFilters.haveWorkers);
    const haveClients = searchParams.get(ClientsFilters.haveClients);
    const haveOpenPositions = searchParams.get(
      ClientsFilters.HaveOpenPositions
    );

    return {
      ...(id && { Id: parseInt(id, 10) }),
      ...(name && { Name: name }),
      ...(haveWorker && { HaveWorkers: haveWorker }),
      ...(haveClients && { HaveClients: haveClients }),
      ...(haveOpenPositions && { HaveOpenPositions: haveOpenPositions }),
      take: parseInt(searchParams.get('take') ?? '10', 10),
      order: parseOrder(searchParams.get('order')),
      page: parseInt(searchParams.get('page') ?? '1', 10),
    };
  }, [searchParams]);

  const searchDefaultValue =
    companiesQuery.Name ?? companiesQuery.Id?.toString() ?? '';
  const { data } = useGetCompaniesPaginatedQuery({
    options: companiesQuery,
  });

  const [filters, setFilters] = useState({
    haveWorker: !!searchParams.get(ClientsFilters.haveWorkers),
    haveClients: !!searchParams.get(ClientsFilters.haveClients),
    haveOpenPositions: !!searchParams.get(ClientsFilters.HaveOpenPositions),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { isDirty },
    watch,
  } = useForm<FormData>({
    defaultValues: {
      search: searchDefaultValue,
    },
  });

  const setSingleSearchParam = (key: string, value: string | number) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set(key, value.toString());
    // Reset page if take has been modified
    if (key === 'take') newSearchParams.set('page', '1');
    setSearchParams(newSearchParams);
  };

  const search = register('search', {
    setValueAs: (value) => value.trim().replace(/\s\s+/g, ' '),
    required: true,
  });

  const searchValue = watch('search');

  const handleSearch = (formData: FormData) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('page', '1');
    if (isNumber(+formData.search)) {
      newSearchParams.set('id', formData.search);
      newSearchParams.delete('name');
    } else {
      newSearchParams.set('name', formData.search);
      newSearchParams.delete('id');
    }
    setSearchParams(newSearchParams);
  };

  const clearSearch = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('id');
    newSearchParams.delete('name');
    setSearchParams(newSearchParams);
    reset({
      search: '',
    });
  };

  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams);

    if (filters.haveWorker)
      newSearchParams.set(ClientsFilters.haveWorkers, '1');
    else newSearchParams.delete(ClientsFilters.haveWorkers);

    if (filters.haveClients)
      newSearchParams.set(ClientsFilters.haveClients, '1');
    else newSearchParams.delete(ClientsFilters.haveClients);

    if (filters.haveOpenPositions)
      newSearchParams.set(ClientsFilters.HaveOpenPositions, '1');
    else newSearchParams.delete(ClientsFilters.HaveOpenPositions);

    setSearchParams(newSearchParams);
  }, [filters, searchParams, setSearchParams]);

  return (
    <div className={classes.content}>
      <header className="flex flex-col md:flex-row gap-8">
        <div className={classes.headerText}>
          <h1 className={classes.title}>{t('SimeraInternal.ClientTitle')}</h1>
        </div>
        <form onSubmit={handleSubmit(handleSearch)}>
          <div className={classes.formContainer}>
            <div
              className={`${classes.searchBarContent} group focus-within:border-internal-main border-2 rounded border-solid px-4`}
            >
              <input
                type="text"
                className={`${classes.searchBar} outline-none w-full`}
                placeholder={t('CandidatesTable.SearchLabel')}
                {...search}
              />
              <HoverTooltip text={t('CompaniesTable.ClearFilters')}>
                <button
                  aria-label="Clear filters"
                  className={`${classes.calendarClearButton} disabled:cursor-not-allowed disabled:opacity-25`}
                  type="button"
                  data-testid="clear-filter-button"
                  onClick={clearSearch}
                  disabled={!searchValue}
                >
                  <ClearIcon className="fill-gray-400" />
                </button>
              </HoverTooltip>
            </div>
            <button
              className={classes.calendarButton}
              disabled={!isDirty}
              type="submit"
              data-testid="apply-filter-button"
            >
              {t('CandidatesTable.ApplyFilter')}
            </button>
          </div>
          <div className="flex flex-row my-2 gap-4 justify-between">
            <div className="flex flex-row items-center justify-center gap-2 text-sm">
              {t('CompaniesTable.CurrentClients')}
              <InputSwitch
                tooltip="Clients that have a Worker currently"
                pt={{
                  slider: ({ props }: InputSwitchPassThroughMethodOptions) => ({
                    className: `${
                      // eslint-disable-next-line react/prop-types
                      props.checked && 'bg-internal-main/50'
                    }`,
                  }),
                }}
                checked={filters.haveWorker}
                onChange={() =>
                  setFilters((prev) => ({
                    ...prev,
                    haveWorker: !prev.haveWorker,
                  }))
                }
              />
            </div>
            <div className="flex flex-row items-center justify-center gap-2 text-sm">
              {t('CompaniesTable.AllClients')}
              <InputSwitch
                tooltip="Have a current or former Worker"
                pt={{
                  slider: ({ props }: InputSwitchPassThroughMethodOptions) => ({
                    className: `${
                      // eslint-disable-next-line react/prop-types
                      props.checked && 'bg-internal-main/50'
                    }`,
                  }),
                }}
                checked={filters.haveClients}
                onChange={() =>
                  setFilters((prev) => ({
                    ...prev,
                    haveClients: !prev.haveClients,
                  }))
                }
              />
            </div>
            <div className="flex flex-row items-center justify-center gap-2 text-sm">
              {t('CompaniesTable.HaveOpenPositions')}
              <InputSwitch
                tooltip="Has an open position"
                pt={{
                  slider: ({ props }: InputSwitchPassThroughMethodOptions) => ({
                    className: `${
                      // eslint-disable-next-line react/prop-types
                      props.checked && 'bg-internal-main/50'
                    }`,
                  }),
                }}
                checked={filters.haveOpenPositions}
                onChange={() =>
                  setFilters((prev) => ({
                    ...prev,
                    haveOpenPositions: !prev.haveOpenPositions,
                  }))
                }
              />
            </div>
          </div>
        </form>
      </header>
      <CompaniesTable data={data?.data ?? []}>
        <TableFooter
          pageState={data?.meta.page ?? 1}
          pageSize={companiesQuery.take ?? 10}
          pageCount={data?.meta.pageCount ?? 0}
          hasPreviousPage={data?.meta.hasPreviousPage ?? false}
          hasNextPage={data?.meta.hasNextPage ?? false}
          nextPage={() =>
            setSingleSearchParam('page', (companiesQuery.page ?? 1) + 1)
          }
          previousPage={() =>
            setSingleSearchParam('page', (companiesQuery.page ?? 2) - 1)
          }
          handleSelectChange={(e) =>
            setSingleSearchParam('take', e.target.value)
          }
        />
      </CompaniesTable>
    </div>
  );
};

export default SimeraEmployersScreen;
